import { useMemo } from 'react';
import { INumberDictionary, IStore, IStringDictionary } from '../../interfaces';
import { IAvailableCompetition, ICompetitionEvolution, ISelectedEvolutions, ITeam } from '../../interfaces/clientInterfaces';
import { useSelector } from 'react-redux';
import { Countries as CountriesUtils } from '../../utils/countries';
import { Localizer } from '../../utils/localizer';
import * as actions from '../../actions';
import { Link } from 'react-router-dom';
import { useAppDispatch } from '../../utils/modules';
import styles from './Countries.module.css';

interface IEditionsInjectedProps {
	teamsById: INumberDictionary<ITeam>;
	evolutions: INumberDictionary<ICompetitionEvolution>; // Evolutions by competition
	availableCompetitions: IAvailableCompetition[];
	selectedEvolutions: ISelectedEvolutions[];
	isMobileViewActive: boolean;
}

interface IProcessedCountry {
	imageCode: string;
	name: string;
	championTimes: number;
	runnerUpTimes: number;
	processedCountries: IStringDictionary<ICountryTimes>;
}

interface ICountry {
	imageCode: string;
	name: string;
	championTimes: number;
	runnerUpTimes: number;
	orderedTeams: ICountryTimes[];
}

interface ICountryTimes {
	team: ITeam;
	championTimes: number;
	runnerUpTimes: number;
}

export default function Countries() {

	const dispatch = useAppDispatch();

	const {
		teamsById,
		evolutions,
		availableCompetitions,
		selectedEvolutions,
		isMobileViewActive,
	} = useSelector(
		(completeState: IStore): IEditionsInjectedProps => {
			return {
				teamsById: completeState.teams.teamsById,
				availableCompetitions: completeState.competitions.availableCompetitions,
				evolutions: completeState.competitions.evolutions,
				selectedEvolutions: completeState.competitions.selectedEvolutions,
				isMobileViewActive: completeState.ui.isMobileViewActive,
			}
		});

	const { orderedCountries } = useMemo(
		(): {
			orderedCountries: ICountry[];
		 } => {
			if (selectedEvolutions.length < 1) {
				return {
					orderedCountries: []
				};
			}
			const competitionName = selectedEvolutions[0].competitionName;
			const competition = availableCompetitions.find(competition => competition.name === competitionName);
			if (!competition) {
				return {
					orderedCountries: []
				};
			}
			const evolution = evolutions[competition.id];
			if (!evolution) {
				return {
					orderedCountries: []
				};
			}

			let processedCountries: IStringDictionary<IProcessedCountry> = { };
			for (const championId in evolution.editions[evolution.editions.length - 1].champions) {
				const team = teamsById[championId];
				if (!team) {
					continue;
				}
				const numberOfChampions = evolution.editions[evolution.editions.length - 1].champions[championId];
				if (!numberOfChampions) {
					continue;
				}
				const countries = CountriesUtils.getTitlesOfFormerCountries(team.countryCode, team.countryCode2 || '', numberOfChampions, competition.regionCode); 
				for (const country of countries) {
					if (country.numberOfTitles === 0) {
						continue;
					}
					const processedCountry = processedCountries[country.countryCode];
					if (typeof processedCountry === 'undefined') {
						processedCountries = {
							...processedCountries,
							[country.countryCode]: {
								imageCode: country.countryCode,
								name: Localizer.getLocalizedCountryName(country.countryCode),
								championTimes: country.numberOfTitles,
								runnerUpTimes: 0,
								processedCountries: {
									[team.teamId]: {
										team,
										runnerUpTimes: 0,
										championTimes: country.numberOfTitles
									}
								}
							}
						};
					} else {
						processedCountries = {
							...processedCountries,
							[country.countryCode]: {
								...processedCountry,
								championTimes: country.numberOfTitles + processedCountry.championTimes,
								processedCountries: {
									...processedCountry.processedCountries,
									[team.teamId]: {
										team,
										runnerUpTimes: 0,
										championTimes: country.numberOfTitles
									}
								}
							}
						};
					}
				}
				
			}
			for (const runnerUpId in evolution.editions[evolution.editions.length - 1].runnerUps) {
				const team = teamsById[runnerUpId];
				if (!team) {
					continue;
				}
				const numberOfRunnerUps = evolution.editions[evolution.editions.length - 1].runnerUps[runnerUpId];
				if (!numberOfRunnerUps) {
					continue;
				}
				const countries = CountriesUtils.getTitlesOfFormerCountries(team.countryCode, team.countryCode2 || '', numberOfRunnerUps, competition.regionCode);
				for (const country of countries) {
					if (country.numberOfTitles === 0) {
						continue;
					}
					const processedCountry = processedCountries[country.countryCode];
					if (typeof processedCountry === 'undefined') {
						processedCountries = {
							...processedCountries,
							[country.countryCode]: {
								imageCode: country.countryCode,
								name: Localizer.getLocalizedCountryName(country.countryCode),
								championTimes: 0,
								runnerUpTimes: country.numberOfTitles,
								processedCountries: {
									[team.teamId]: {
										team,
										runnerUpTimes: country.numberOfTitles,
										championTimes: 0
									}
								}
							}
						};
					} else {
						const runnerUpTeam = processedCountry.processedCountries[team.teamId] || {
							team,
							runnerUpTimes: 0,
							championTimes: 0	
						};
						processedCountries = {
							...processedCountries,
							[country.countryCode]: {
								...processedCountry,
								runnerUpTimes: country.numberOfTitles + processedCountry.runnerUpTimes,
								processedCountries: {
									...processedCountry.processedCountries,
									[team.teamId]: {
										...runnerUpTeam,
										runnerUpTimes: country.numberOfTitles
									}
								}
							}
						};
					}
				}
			}
			const unorderedCountries: ICountry[] = [];
			for (const processedCountry in processedCountries) {
				const country = processedCountries[processedCountry];
				if (!country) {
					continue;
				}
				const teams: ICountryTimes[] = [];
				for (const processedTeam in country.processedCountries) {
					const countryTime = country.processedCountries[processedTeam];
					if (!countryTime) {
						continue;
					}
					teams.push(countryTime)
				}
				const orderedTeams = teams.sort((a: ICountryTimes, b: ICountryTimes) => {
					if (a.championTimes > b.championTimes) {
						return -1;
					} else if (a.championTimes < b.championTimes) {
						return 1;
					} else {
						if (a.runnerUpTimes > b.runnerUpTimes) {
							return -1;
						} else if (a.runnerUpTimes <= b.runnerUpTimes) {
							return 1;
						}
					}
					return 1;
				});
				unorderedCountries.push({
					...country,
					orderedTeams
				})
			}
			const orderedCountries = unorderedCountries.sort((a: ICountry, b: ICountry) => {
				if (a.championTimes > b.championTimes) {
					return -1;
				} else if (a.championTimes < b.championTimes) {
					return 1;
				} else {
					if (a.runnerUpTimes > b.runnerUpTimes) {
						return -1;
					} else if (a.runnerUpTimes <= b.runnerUpTimes) {
						return 1;
					}
				}
				return -1;
			});
			return {
				orderedCountries
			};
		},
		[teamsById, selectedEvolutions, evolutions, availableCompetitions]
	);

	const renderCountry = (year: number, countryCode: string, countryName: string) => {
		return (
			<Link to={`/${countryName}`}>
				<img
					alt={countryName}
					className={styles.flagImage}
					src={`../../../img/flag/${CountriesUtils.getFlagOfCountry(year, countryCode, countryCode, countryCode, false)}.png`}
				/>
				<div className={styles.nameContainer}>{countryName}</div>
			</Link>
		);
	}

	const renderTeam = (team: ITeam) => {
		return (
			<span className={styles.teamContainer}>
				<div className={styles.shieldAndFlagContainer}>
					<img
						alt={team.name}
						className={isMobileViewActive ? styles.mobileShieldImage : styles.shieldImage}
						src={`../../../img/shield/${team.countryCode}_${team.teamCode}.png`}
					/>
					<div className={styles.nameContainer}>{team.shortName || team.name}</div>
				</div>
				<div>
					<img
						alt={team.name}
						className={isMobileViewActive ? styles.mobileScarfImage : styles.scarfImage}
						src={`../../../img/scarf/${team.countryCode}_${team.teamCode}.jpg`}
					/>
				</div>
			</span>
		);
	}

	return (
		<div className={styles.evolutionContainer}>
			<table className={isMobileViewActive ? styles.mobileTable : styles.table}>
				<thead>
					<th className={styles.rankingHeader} scope='col'>
						{'#'}
					</th>
					<th className={styles.countryHeader} scope='col'>
						{'COUNTRY'}
					</th>
					<th scope='col'>
						{'CLUB'}
					</th>
					<th scope='col'>
						{isMobileViewActive ? '1st' : 'TITLES'}
					</th>
					<th scope='col'>
						{isMobileViewActive ? '2nd' : 'RUNNER-UP'}
					</th>
				</thead>
				<tbody>
					{orderedCountries.map((orderedCountry, countryIndex) => {
						return (
							<>
								{orderedCountry.orderedTeams.map((team, idx) => {
									if (idx === 0) {
										return (
											<tr className={styles.teamRow} >
												<th
													className={styles.rankingHeader}
													scope='row'
													rowSpan={orderedCountry.orderedTeams.length > 1 ? orderedCountry.orderedTeams.length + 1 : 1}
												>
													{countryIndex + 1}
												</th>
												<td
													className={styles.countryHeader}
													rowSpan={orderedCountry.orderedTeams.length > 1 ? orderedCountry.orderedTeams.length + 1 : 1}
												>
													{renderCountry(2023, orderedCountry.imageCode, orderedCountry.name)}
												</td>
												<td
													className={styles.teamCell}
													onClick={e => dispatch(actions.setHomeDetailTeam(team.team.teamId))}>
													{renderTeam(team.team)}
												</td>
												<td>
													{team.championTimes}
												</td>
												<td>
													{team.runnerUpTimes}
												</td>
											</tr>
										);
									}
									return (
										<tr className={styles.teamRow} >
											<td
												className={styles.teamCell}
												onClick={e => dispatch(actions.setHomeDetailTeam(team.team.teamId))}
											>
												{renderTeam(team.team)}
											</td>
											<td>
												{team.championTimes}
											</td>
											<td>
												{team.runnerUpTimes}
											</td>
										</tr>
									);
								})}
								{
									orderedCountry.orderedTeams.length > 1 
										? <tr className={styles.teamRow} >
											<td className={styles.resultTd}>
												{`${orderedCountry.orderedTeams.length} teams`}
											</td>
											<td className={styles.resultTd}>
												{orderedCountry.championTimes}
											</td>
											<td className={styles.resultTd}>
												{orderedCountry.runnerUpTimes}
											</td>
										</tr>
										: null
								}
							</>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

